import { Component, computed, inject, input } from '@angular/core';
import { AccessGroupUsersSearchEvent, AccessThrough, ChargingUser, AccessGroup } from '../charging-user.model';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { IdTag, IdTagSearchEvent } from '../../id-tags/id-tag-model';
import { combineLatest, map, Observable, startWith, Subject, switchMap, tap } from 'rxjs';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { PageEvent } from '@angular/material/paginator';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { RouterLink } from '@angular/router';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';
import { EmptyDashPipe } from '../../../shared/pipes/empty-dash.pipe';
import { MatDialog } from '@angular/material/dialog';
import { AppEmulatorForAccessComponent } from '../app-emulator-for-access/app-emulator-for-access.component';
import { ChargingUserPreviousAccessGroupsModalComponent } from '../charging-user-previous-accessgroups-modal/charging-user-previous-accessgroups-modal.component';
import { IdTagListQueryParams } from '../../id-tags/id-tags-overview/id-tag-list.state.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import {AccessGroupUserType} from "../../../shared/enums/access-group-user-type";

@Component({
  selector: 'adm-charging-user-details',
  templateUrl: './charging-user-details.component.html',
  styleUrls: ['./charging-user-details.component.scss'],
  imports: [RouterLink, DkCurrencyPipe, EmptyDashPipe, MatIcon, MatTooltip, DecimalPipe, DatePipe, AsyncPipe],
})
export class ChargingUserDetailsComponent {
  private idTagService = inject(IdTagService);
  private chargingUserService = inject(ChargingUserService);
  private dialog = inject(MatDialog);

  public refresh$ = new Subject<void>();
  public accessGroupsMain: AccessGroup[] = [];
  public accessGroupsByDeviceId: AccessGroup[] = [];
  public accessGroupsByMokUserNumber: AccessGroup[] = [];

  chargingUser = input.required<ChargingUser>();

  readonly idTagSearchEvent = computed(() => {
    const cu = this.chargingUser();
    return {
      paging: new PageEvent(),
      includeInactive: true,
      chargingUserId: cu.id,
    } as IdTagSearchEvent;
  });

  readonly accessGroupSearchEvent = computed(() => {
    const cu = this.chargingUser();
    return {
      paging: { pageIndex: 0, pageSize: 100 },
      chargingUserIdentifier: cu.identifier,
      deviceId: cu.deviceId,
      mokUserNumber: cu.mokUserNumber,
      includeDeleted: false,
    } as AccessGroupUsersSearchEvent;
  });

  public idTags$: Observable<IdTag[]> = combineLatest([toObservable(this.idTagSearchEvent), this.refresh$.pipe(startWith(void 0))]).pipe(
    switchMap(([event]) => this.idTagService.getIdTags(event)),
    map(result => result.items)
  );

  public accessGroups$ = combineLatest([toObservable(this.accessGroupSearchEvent), this.refresh$.pipe(startWith(void 0))]).pipe(
    switchMap(([event]) => this.chargingUserService.getAccessGroupUsers(event)),
    tap(result => {
      this.accessGroupsMain = result.items.filter(group => group.accessThrough === AccessThrough.ChargingUserIdentifier);
      this.accessGroupsByDeviceId = result.items
        .filter(group => group.accessThrough === AccessThrough.DeviceId && group.deleted === null)
        .filter((group, index, self) => index === self.findIndex(g => g.identifier === group.identifier));
      this.accessGroupsByMokUserNumber = result.items
        .filter(group => group.accessThrough === AccessThrough.MokUserNumber && group.deleted === null)
        .filter((group, index, self) => index === self.findIndex(g => g.identifier === group.identifier));
    }),
    map(result => result.items)
  );

  readonly chargingUserDetails$ = combineLatest([toObservable(this.chargingUser), this.refresh$.pipe(startWith(void 0))]).pipe(
    switchMap(([cu]) => this.chargingUserService.getChargingUser(cu.id))
  );

  public idTagStatusEnum = IdTagStatusEnum;
  public idTagListQueryParams = IdTagListQueryParams;

  public hasCompanyCar = computed(
    () => this.chargingUser().idTagUserType.toString() === IdTagUserType[this.idTagUserType.CompanyCar].toString()
  );

  public CompanyCarDetermined = computed(
    () => this.chargingUser().idTagUserType.toString() !== IdTagUserType[this.idTagUserType.NotDetermined].toString()
  );

  public idTagUserType: typeof IdTagUserType = IdTagUserType;
  public discountType: typeof DiscountType = DiscountType;
  public accessGroupUserType: typeof AccessGroupUserType = AccessGroupUserType;

  idTagsQueryParams = computed(() => ({
    [this.idTagListQueryParams.chargingUserId]: this.chargingUser()?.id,
    [this.idTagListQueryParams.includeInactive]: true,
  }));

  idTagQueryParams(idTag: IdTag) {
    const params: Record<string, any> = {
      query: idTag.idToken,
      idTagStatus: idTag.status,
    };

    // Add includeInactive if status is Inactive, NoIdTagGroup, or Blocked
    if (
      idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.Inactive] ||
      idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.NoIdTagGroup] ||
      idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.Blocked]
    ) {
      params['includeInactive'] = true;
    }

    return params;
  }

  public refresh() {
    this.refresh$.next();
  }

  public async openAppEmulatorForAccess(chargingUser: ChargingUser) {
    if (!chargingUser.deviceId && !chargingUser.mokUserNumber) {
      return;
    }

    this.dialog.open(AppEmulatorForAccessComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '531px',
      height: '1151px',
      panelClass: 'app-emulator-for-access-modal',
    });
  }

  public openPreviousAccessGroups(chargingUser: ChargingUser) {
    this.dialog.open(ChargingUserPreviousAccessGroupsModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '850px',
    });
  }

  protected readonly AccessGroupUserType = AccessGroupUserType;
}
